import { useRef, useEffect, useCallback } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

interface AutoGrowTextareaProps extends Omit<React.ComponentProps<typeof Textarea>, 'rows'> {
  minRows?: number;
  maxRows?: number;
}

/**
 * Textarea that auto-grows with content, bounded by minRows and maxRows.
 * Uses the native textarea with dynamic height adjustment.
 */
export function AutoGrowTextarea({
  minRows = 2,
  maxRows = 8,
  className,
  onChange,
  value,
  defaultValue,
  ...props
}: AutoGrowTextareaProps): React.ReactElement {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset height to measure scrollHeight correctly
    textarea.style.height = 'auto';

    // Compute line height from computed styles
    const computed = window.getComputedStyle(textarea);
    const lineHeight = parseFloat(computed.lineHeight) || 20;
    const paddingTop = parseFloat(computed.paddingTop) || 0;
    const paddingBottom = parseFloat(computed.paddingBottom) || 0;
    const borderTop = parseFloat(computed.borderTopWidth) || 0;
    const borderBottom = parseFloat(computed.borderBottomWidth) || 0;

    const minHeight = lineHeight * minRows + paddingTop + paddingBottom + borderTop + borderBottom;
    const maxHeight = lineHeight * maxRows + paddingTop + paddingBottom + borderTop + borderBottom;

    const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight);
    textarea.style.height = `${newHeight}px`;

    // Show scrollbar only when content exceeds max
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, [minRows, maxRows]);

  // Adjust on mount and when value changes
  useEffect(() => {
    adjustHeight();
  }, [value, defaultValue, adjustHeight]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    adjustHeight();
    onChange?.(e);
  };

  return (
    <Textarea
      ref={textareaRef}
      className={cn('resize-none', className)}
      onChange={handleChange}
      value={value}
      defaultValue={defaultValue}
      rows={minRows}
      {...props}
    />
  );
}
